import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from "@angular/core";
import { TypeManagerDecorator } from "../main/type.map.service";
import { S25IconComponent } from "../modules/s25-icon/s25.icon.component";

const SESSION_STORAGE_PAUSED_KEY = "connect25_hero_slideshow_paused";

function readPausedFromSession(): boolean {
    try {
        return sessionStorage.getItem(SESSION_STORAGE_PAUSED_KEY) === "true";
    } catch {
        return false;
    }
}

function writePausedToSession(paused: boolean): void {
    try {
        sessionStorage.setItem(SESSION_STORAGE_PAUSED_KEY, String(paused));
    } catch {
        // storage blocked — in-memory only for this page load
    }
}

/**
 * Full-bleed rotating hero images (filestore URLs, HTTPS, or data URLs).
 * When {@link imageUrls} has fewer than two entries, no rotation runs.
 */
@TypeManagerDecorator("s25-se-hero-slideshow")
@Component({
    selector: "s25-se-hero-slideshow",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25IconComponent],
    host: {
        class: "s25-se-hero-slideshow",
        "[attr.data-reduced-motion]": "reducedMotion() ? '' : null",
        "[attr.data-paused]": "paused() ? '' : null",
    },
    template: `
        @for (url of imageUrls(); track $index) {
            <img
                [src]="url"
                alt=""
                class="s25-se-hero-slideshow__img"
                [class.s25-se-hero-slideshow__img--active]="$index === activeIndex()"
                loading="lazy"
            />
        }
        @if (showPauseControl()) {
            <button
                type="button"
                class="s25-se-hero-slideshow__control"
                data-testid="hero-slideshow-pause-control"
                [attr.aria-pressed]="paused()"
                [attr.aria-label]="paused() ? 'Resume slideshow' : 'Pause slideshow'"
                (click)="togglePaused()"
            >
                <s25-ng-icon [type]="paused() ? 'play' : 'pause'" [ariaLabel]="false" />
                <span class="s25-se-hero-slideshow__control-text">
                    {{ paused() ? "Resume slideshow" : "Pause slideshow" }}
                </span>
            </button>
        }
    `,
    styles: `
        :host {
            display: block;
            position: absolute;
            inset: 0;
            overflow: hidden;
            pointer-events: none;
            --slideshow-control-bg: color-mix(in srgb, var(--connect25-banner-bg, var(--cn-fg-1)) 85%, transparent);
            --slideshow-control-border: 1px solid
                color-mix(in srgb, var(--connect25-banner-fg, var(--color--surface-bg)) 40%, transparent);
        }
        .s25-se-hero-slideshow__img {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center top;
            opacity: 0;
            transition: opacity 1.1s ease-in-out;
        }
        :host([data-paused]) .s25-se-hero-slideshow__img {
            transition: none;
        }
        .s25-se-hero-slideshow__img--active {
            opacity: 1;
        }
        .s25-se-hero-slideshow__control {
            pointer-events: auto;
            position: absolute;
            z-index: 1;
            top: 0.75rem;
            right: 0.75rem;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 0.35rem;
            margin: -1px;
            padding: 0;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            width: 1px;
            height: 1px;
            white-space: nowrap;
            border: 0;
            background: transparent;
            color: var(--connect25-banner-fg, var(--color--surface-bg));
            cursor: pointer;
        }
        :host-context(.hero-search-section:hover) .s25-se-hero-slideshow__control,
        .s25-se-hero-slideshow__control:focus-visible,
        .s25-se-hero-slideshow__control:focus-within {
            margin: 0;
            overflow: visible;
            clip: auto;
            width: auto;
            height: auto;
            padding: 0.5rem 0.75rem;
            white-space: normal;
            background: var(--slideshow-control-bg);
            border: var(--slideshow-control-border);
            border-radius: 4px;
            box-shadow: var(--shadow--single-focus);
            outline: none;
        }
        @media (hover: none), (pointer: coarse) {
            .s25-se-hero-slideshow__control-text {
                display: none;
            }
            :host-context(.hero-search-section:hover) .s25-se-hero-slideshow__control,
            .s25-se-hero-slideshow__control:focus-visible,
            .s25-se-hero-slideshow__control:focus-within {
                padding: 0.5rem;
            }
        }
    `,
})
export class S25SeHeroSlideshowComponent {
    readonly imageUrls = input<string[]>([]);
    readonly intervalMs = input(8000);

    readonly activeIndex = signal(0);
    readonly reducedMotion = signal(
        typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches,
    );
    readonly paused = signal(readPausedFromSession());

    readonly showPauseControl = computed(() => this.imageUrls().length >= 2 && !this.reducedMotion());

    readonly autoAdvanceEnabled = computed(() => this.showPauseControl() && !this.paused());

    constructor() {
        effect((onCleanup) => {
            const urls = this.imageUrls();
            const shouldAutoAdvance = this.autoAdvanceEnabled();

            if (!shouldAutoAdvance) {
                if (!this.paused() || urls.length < 2 || this.reducedMotion()) {
                    this.activeIndex.set(0);
                }
                return;
            }

            const timer = setInterval(() => {
                this.activeIndex.update((index) => (index + 1) % urls.length);
            }, this.intervalMs());

            onCleanup(() => clearInterval(timer));
        });
    }

    togglePaused(): void {
        this.paused.update((wasPaused) => {
            const nowPaused = !wasPaused;
            writePausedToSession(nowPaused);
            return nowPaused;
        });
    }
}
